import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, inr, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Input } from "@/components/ui/input";
import { Mail, ShieldCheck, CalendarDays, MapPin, RotateCcw, Store, PackageOpen } from "lucide-react";
import { toast } from "sonner";

const PLAN_LABEL = { full: "100% paid", half: "50% now", quarter: "25% now", on_event: "On event day" };
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const TOKEN_KEY = "bme_orders_token";

export default function OrderHistory() {
  const navigate = useNavigate();
  const { addItems } = useCart();
  const [step, setStep] = useState("email"); // email | code | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoChecking, setAutoChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setAutoChecking(false); return; }
    (async () => {
      try {
        const { data } = await api.post("/bookings/session", { token });
        setEmail(data.email || "");
        setRows(data.bookings || []);
        setStep("done");
      } catch {
        localStorage.removeItem(TOKEN_KEY);
      } finally {
        setAutoChecking(false);
      }
    })();
  }, []);

  const sendCode = async () => {
    const val = email.trim();
    if (!val || !val.includes("@")) return toast.error("Enter a valid email");
    setLoading(true);
    try {
      await api.post("/bookings/otp/request", { email: val });
      toast.success("If you have bookings, a 6-digit code is on its way to your email");
      setStep("code");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    if (!code.trim()) return toast.error("Enter the code from your email");
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/otp/verify", { email: email.trim(), code: code.trim(), remember });
      setRows(data.bookings || []);
      if (remember && data.token) localStorage.setItem(TOKEN_KEY, data.token);
      setStep("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const forget = () => {
    localStorage.removeItem(TOKEN_KEY);
    setStep("email"); setEmail(""); setCode(""); setRemember(false); setRows([]);
    toast.success("This device has been forgotten");
  };

  const rebook = (booking) => {
    const items = booking.items || [];
    if (!items.length) return toast.error("This booking has no items to rebook");
    addItems(items);
    toast.success("Added to your cart");
    navigate("/cart");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-8 pb-24" data-testid="order-history">
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">My bookings</h1>
      <p className="text-muted-foreground mt-2">Verify your email to view your past events securely and rebook a favourite vendor in one tap.</p>

      {autoChecking && <p className="mt-6 text-muted-foreground" data-testid="order-auto-checking">Checking this device…</p>}

      {!autoChecking && step === "email" && (
        <div className="mt-6 bg-card border border-stone-200 rounded-2xl p-5 max-w-md">
          <label className="text-sm font-semibold flex items-center gap-2 mb-2"><Mail className="h-4 w-4 text-primary" /> Your email</label>
          <Input
            data-testid="order-email-input"
            type="email"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendCode()}
            className="h-12"
          />
          <button data-testid="order-send-code" onClick={sendCode} disabled={loading} className="mt-3 w-full h-12 bg-primary text-white rounded-full font-semibold disabled:opacity-60">
            {loading ? "Sending…" : "Send verification code"}
          </button>
        </div>
      )}

      {!autoChecking && step === "code" && (
        <div className="mt-6 bg-card border border-stone-200 rounded-2xl p-5 max-w-md">
          <label className="text-sm font-semibold flex items-center gap-2 mb-1"><ShieldCheck className="h-4 w-4 text-primary" /> Enter the 6-digit code</label>
          <p className="text-xs text-muted-foreground mb-3">Sent to <b>{email}</b>. It expires in 10 minutes.</p>
          <Input
            data-testid="order-code-input"
            inputMode="numeric"
            maxLength={6}
            placeholder="••••••"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && verify()}
            className="h-12 text-center text-2xl tracking-[0.5em]"
          />
          <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer">
            <input data-testid="order-remember" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-[var(--primary)]" />
            Remember me on this device (skip the code next time)
          </label>
          <button data-testid="order-verify" onClick={verify} disabled={loading} className="mt-3 w-full h-12 bg-primary text-white rounded-full font-semibold disabled:opacity-60">
            {loading ? "Verifying…" : "View my bookings"}
          </button>
          <div className="flex justify-between mt-3 text-sm">
            <button data-testid="order-change-email" onClick={() => { setStep("email"); setCode(""); }} className="text-muted-foreground hover:text-foreground">Change email</button>
            <button data-testid="order-resend" onClick={sendCode} disabled={loading} className="text-primary font-semibold">Resend code</button>
          </div>
        </div>
      )}

      {!autoChecking && step === "done" && (
        <>
          <div className="mt-6 flex items-center justify-between flex-wrap gap-2">
            <p className="text-sm text-emerald-700 font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Verified · {email}</p>
            <div className="flex items-center gap-4">
              {localStorage.getItem(TOKEN_KEY) && <button data-testid="order-forget" onClick={forget} className="text-sm text-muted-foreground hover:text-foreground">Forget this device</button>}
              <button data-testid="order-restart" onClick={() => { setStep("email"); setEmail(""); setCode(""); setRows([]); }} className="text-sm text-primary font-semibold">Use another email</button>
            </div>
          </div>

          {rows.length === 0 && (
            <div className="text-center py-16 text-muted-foreground" data-testid="order-empty">
              <PackageOpen className="h-10 w-10 mx-auto mb-3 opacity-60" />
              <p>No bookings found for this email.</p>
            </div>
          )}

          <div className="mt-4 grid gap-4">
            {rows.map((b) => (
              <div key={b.id} data-testid={`order-card-${b.id}`} className="bg-card border border-stone-200 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-display font-bold text-lg">{b.event_type || "Event"}</h3>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" /> {fmtDate(b.event_date)}</span>
                      {(b.venue || b.city) && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {[b.venue, b.city].filter(Boolean).join(", ")}</span>}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full font-semibold capitalize ${b.status === "cancelled" ? "bg-rose-100 text-rose-700" : b.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{b.status || "pending"}</span>
                    <p className="font-display font-extrabold text-lg mt-1">{inr(b.final_total ?? b.subtotal ?? 0)}</p>
                    <p className="text-xs text-muted-foreground">{PLAN_LABEL[b.payment_plan] || "100% paid"} · booked {fmtDate(b.created_at)}</p>
                  </div>
                </div>

                {(b.items || []).length > 0 && (
                  <div className="mt-4 border border-stone-200 rounded-xl overflow-hidden">
                    {b.items.map((it, i) => (
                      <div key={i} className="flex items-center gap-3 px-3 py-2 border-b border-stone-100 last:border-0">
                        {it.image ? <img src={imgUrl(it.image)} alt={it.name} className="h-12 w-12 rounded-lg object-cover shrink-0" /> : <div className="h-12 w-12 rounded-lg bg-muted shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm leading-tight">{it.name} <span className="text-muted-foreground">× {it.quantity}</span></p>
                          <button
                            data-testid={`order-vendor-${b.id}-${i}`}
                            onClick={() => it.vendor_id && navigate(`/vendors/${it.vendor_id}`)}
                            className="text-xs text-primary font-semibold flex items-center gap-1 hover:underline"
                          >
                            <Store className="h-3 w-3" /> {it.vendor_name}
                          </button>
                        </div>
                        <span className="font-semibold text-sm whitespace-nowrap">{inr(it.price * it.quantity)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {b.status !== "cancelled" && (b.items || []).length > 0 && (
                  <button
                    data-testid={`order-rebook-${b.id}`}
                    onClick={() => rebook(b)}
                    className="mt-4 flex items-center gap-2 h-11 px-5 bg-primary text-white rounded-full text-sm font-semibold hover:bg-primary/90 transition-colors"
                  >
                    <RotateCcw className="h-4 w-4" /> Rebook this event
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
